import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { GET_PENDING_APPROVAL_CARD } from "graphql/card";
import { GET_REWARDS_BALANCE } from "graphql/company";
import { GET_PAYMENTS } from "graphql/payment";
import { GET_INVOICES } from "graphql/invoice";
import { GET_TRANSACTIONS } from "graphql/transaction";
import PaymentStatus from "enums/PaymentStatus";
import { InvoiceStatus } from "enums/InvoiceStatus";
import TransactionStatus from "enums/TransactionStatus";
import { REIMBURSEMENT_TYPES } from "utils/transactions/transactionGroups";

/** The worst item in a queue, for the insight that names it. */
export interface QueueItem {
  name: string;
  amount: number;
  /** How many days past due, or how long it has been waiting. */
  days: number;
}

/** What every dashboard queue tile and insight reads. */
export interface QueueSummary {
  count: number;
  /**
   * The sample filled up, so `count` and `total` are floors rather than
   * totals. `pageInfo.total` on these queries returns the size of the page it
   * just returned, not the size of the collection, so there is no way to ask
   * how much was left behind: a tile that says "250" when the sample is 250
   * has to say so.
   */
  capped: boolean;
  /** Money owed. Zero for a queue that is not about money. */
  total: number;
  overdue: number;
  oldest: QueueItem | null;
  loading: boolean;
}

/**
 * One page per queue. None of these list queries expose an aggregate, so a
 * count of what is overdue has to look at rows; a queue deep enough to page
 * is past the point where a dashboard tile is the right place to read it.
 */
const QUEUE_SAMPLE = 250;

/**
 * How long an approval may sit before the dashboard calls it overdue. Payments
 * and invoices carry a real `dueDate`; approvals carry nothing, so this is the
 * dashboard's own display threshold and not a policy the rest of the app knows
 * about. It exists here so the two approval queues cannot disagree.
 */
const APPROVAL_SLA_DAYS = 3;

/**
 * A queue behind an entitlement is not fetched when the entitlement is off:
 * its tile is hidden and its insight cannot rank, so the request buys nothing.
 * A skipped queue reads as an empty, settled one.
 */
export interface QueueOptions {
  skip?: boolean;
}

const daysPast = (when?: number | null): number => {
  if (!when) return 0;
  return Math.max(dayjs().startOf("day").diff(dayjs(when), "day"), 0);
};

/** The item that has been waiting longest, or null when the queue is empty. */
const worstOf = (items: QueueItem[]): QueueItem | null =>
  items.reduce<QueueItem | null>(
    (worst, item) => (!worst || item.days > worst.days ? item : worst),
    null,
  );

interface Contact {
  businessName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

/** A contact's display name, business first: an invoice is owed by a company. */
const contactName = (contact?: Contact | null): string =>
  contact?.businessName ||
  [contact?.firstName, contact?.lastName].filter(Boolean).join(" ") ||
  "";

interface CardApprovalQuery {
  pendingCardApproval?: {
    edges?:
      | {
          node: {
            id: string;
            user?: {
              firstName?: string | null;
              lastName?: string | null;
            } | null;
            pendingRequest?: { requestDate?: number | null } | null;
          };
        }[]
      | null;
  } | null;
}

/** Card and limit requests waiting on an approver. */
export const useCardRequests = (): QueueSummary => {
  const { data, loading } = useQuery<CardApprovalQuery>(
    GET_PENDING_APPROVAL_CARD,
    { variables: { first: QUEUE_SAMPLE }, fetchPolicy: "cache-and-network" },
  );

  return useMemo(() => {
    const rows = (data?.pendingCardApproval?.edges ?? []).map(({ node }) => ({
      name: contactName(node.user),
      amount: 0,
      days: daysPast(node.pendingRequest?.requestDate),
    }));

    return {
      count: rows.length,
      capped: rows.length >= QUEUE_SAMPLE,
      total: 0,
      overdue: rows.filter((row) => row.days >= APPROVAL_SLA_DAYS).length,
      oldest: worstOf(rows),
      loading,
    };
  }, [data, loading]);
};

interface PaymentsQuery {
  payments?: {
    edges?:
      | {
          node: {
            id: string;
            amount?: number | null;
            dueDate?: number | null;
            payTo?: Contact | null;
          };
        }[]
      | null;
  } | null;
}

const OUTSTANDING_PAYMENT_STATUSES = [
  PaymentStatus.DRAFT,
  PaymentStatus.PENDING,
  PaymentStatus.INSUFFICIENT_FUNDS,
  PaymentStatus.PAYMENT_FAILED,
];

/** Bills entered but not yet paid. */
export const useOutstandingPayments = ({
  skip,
}: QueueOptions = {}): QueueSummary => {
  const { data, loading } = useQuery<PaymentsQuery>(GET_PAYMENTS, {
    variables: { first: QUEUE_SAMPLE, status: OUTSTANDING_PAYMENT_STATUSES },
    fetchPolicy: "cache-and-network",
    skip,
  });

  return useMemo(() => {
    const rows = (data?.payments?.edges ?? []).map(({ node }) => ({
      name: contactName(node.payTo),
      amount: Math.abs(node.amount ?? 0),
      days: daysPast(node.dueDate),
    }));

    return {
      count: rows.length,
      capped: rows.length >= QUEUE_SAMPLE,
      total: rows.reduce((sum, row) => sum + row.amount, 0),
      overdue: rows.filter((row) => row.days > 0).length,
      oldest: worstOf(rows.filter((row) => row.days > 0)),
      loading,
    };
  }, [data, loading]);
};

interface InvoicesQuery {
  invoices?: {
    edges?:
      | {
          node: {
            id: string;
            total?: number | null;
            dueDate?: number | null;
            primaryRecipient?: Contact | null;
          };
        }[]
      | null;
  } | null;
}

const OUTSTANDING_INVOICE_STATUSES = [
  InvoiceStatus.SENT,
  InvoiceStatus.PENDING,
  InvoiceStatus.PROCESSING_PAYMENT,
  InvoiceStatus.PAYMENT_FAILED,
];

/** Invoices sent but not yet collected. */
export const useOutstandingInvoices = ({
  skip,
}: QueueOptions = {}): QueueSummary => {
  const { data, loading } = useQuery<InvoicesQuery>(GET_INVOICES, {
    variables: { first: QUEUE_SAMPLE, status: OUTSTANDING_INVOICE_STATUSES },
    fetchPolicy: "cache-and-network",
    skip,
  });

  return useMemo(() => {
    const rows = (data?.invoices?.edges ?? []).map(({ node }) => ({
      name: contactName(node.primaryRecipient),
      amount: Math.abs(node.total ?? 0),
      days: daysPast(node.dueDate),
    }));

    return {
      count: rows.length,
      capped: rows.length >= QUEUE_SAMPLE,
      total: rows.reduce((sum, row) => sum + row.amount, 0),
      overdue: rows.filter((row) => row.days > 0).length,
      oldest: worstOf(rows.filter((row) => row.days > 0)),
      loading,
    };
  }, [data, loading]);
};

interface ReimbursementsQuery {
  transactions?: {
    edges?:
      | {
          node: {
            id: string;
            amount?: number | null;
            createdAt?: number | null;
            user?: {
              firstName?: string | null;
              lastName?: string | null;
            } | null;
          };
        }[]
      | null;
  } | null;
}

/** Out-of-pocket spend waiting on approval or payout. */
export const useReimbursementsDue = (): QueueSummary => {
  const { data, loading } = useQuery<ReimbursementsQuery>(GET_TRANSACTIONS, {
    variables: {
      first: QUEUE_SAMPLE,
      type: REIMBURSEMENT_TYPES,
      transactionStatus: [
        TransactionStatus.PENDING,
        TransactionStatus.PENDING_BANK_ACCOUNT,
      ],
    },
    fetchPolicy: "cache-and-network",
  });

  return useMemo(() => {
    const rows = (data?.transactions?.edges ?? []).map(({ node }) => ({
      name: contactName(node.user),
      amount: Math.abs(node.amount ?? 0),
      days: daysPast(node.createdAt),
    }));

    return {
      count: rows.length,
      capped: rows.length >= QUEUE_SAMPLE,
      total: rows.reduce((sum, row) => sum + row.amount, 0),
      overdue: rows.filter((row) => row.days >= APPROVAL_SLA_DAYS).length,
      oldest: worstOf(rows),
      loading,
    };
  }, [data, loading]);
};

interface RewardsQuery {
  getRewardsBalance?: {
    available?: { amount?: number | null } | null;
  } | null;
}

/**
 * Rewards available to redeem. Not a queue (nothing is waiting on an approver),
 * but it reads as one of the same tiles: a title, an amount and a line saying
 * what the amount is for.
 */
export const useRewardsBalance = ({
  skip,
}: QueueOptions = {}): QueueSummary => {
  const { data, loading } = useQuery<RewardsQuery>(GET_REWARDS_BALANCE, {
    fetchPolicy: "network-only",
    skip,
  });

  return useMemo(
    () => ({
      count: 0,
      capped: false,
      total: data?.getRewardsBalance?.available?.amount ?? 0,
      overdue: 0,
      oldest: null,
      loading,
    }),
    [data, loading],
  );
};

export { APPROVAL_SLA_DAYS };
